import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { DollarSign, Settings, FileText, Tag, BarChart3, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { CAPS, hasCapability } from '../../utils/capabilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFinancesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Phase 4e: Plans + Razorpay settings stay admin-only; view_finances holders
  // see the read-only cards (Sales, Payment Logs) plus KPI badges on Plans/Coupons.
  const isFullAdmin = user?.role === 'admin';
  const canViewFinances = isFullAdmin || hasCapability(user, CAPS.VIEW_FINANCES);
  const [isConfigured, setIsConfigured] = useState(false);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalTransactions: 0,
    successfulTransactions: 0,
    paidUsers: 0,
    activePlans: 0,
    activeCoupons: 0
  });

  // Hide-not-gate: if a caller reaches this via direct URL without the
  // view_finances cap (and isn't a full admin), send them home.
  useEffect(() => {
    if (user && !canViewFinances) {
      navigate('/admin', { replace: true });
    }
  }, [user, canViewFinances, navigate]);

  useEffect(() => {
    if (isFullAdmin) checkConfiguration();  // payment-settings endpoint is admin-only
    fetchStats();
    fetchPlansAndCoupons();
  }, [isFullAdmin]);

  const checkConfiguration = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment-settings`);
      setIsConfigured(response.data.configured || false);
    } catch (error) {
      console.error('Failed to check configuration:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      // Also fetch payment logs for total transactions count
      const logsResponse = await axios.get(`${API}/admin/payment-logs?per_page=1`);
      setStats(prev => ({
        ...prev,
        totalRevenue: response.data.total_revenue || 0,
        successfulTransactions: response.data.successful_payments || 0,
        paidUsers: response.data.paid_users || 0,
        totalTransactions: logsResponse.data.total || 0
      }));
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchPlansAndCoupons = async () => {
    try {
      const [plansRes, couponsRes] = await Promise.all([
        axios.get(`${API}/admin/plans`),
        axios.get(`${API}/admin/coupons`)
      ]);
      const activePlans = (plansRes.data || []).filter(p => p.is_active).length;
      const activeCoupons = (couponsRes.data || []).filter(c => c.is_active).length;
      setStats(prev => ({
        ...prev,
        activePlans,
        activeCoupons
      }));
    } catch (error) {
      console.error('Failed to fetch plans/coupons:', error);
    }
  };

  const financeCards = [
    {
      title: 'Sales Overview & Analytics',
      description: 'View revenue trends, sales reports, and financial analytics',
      icon: BarChart3,
      path: '/admin/finances/analytics',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      stats: [
        { label: 'Total Revenue', value: `₹${stats.totalRevenue.toFixed(0)}` },
        { label: 'Paid Users', value: stats.paidUsers }
      ],
      comingSoon: false,
      testId: 'finance-card-sales'
    },
    {
      title: 'Payment Logs',
      description: 'View all payment transactions, success/failed attempts, and user details',
      icon: FileText,
      path: '/admin/finances/logs',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      stats: [
        { label: 'Total Transactions', value: stats.totalTransactions },
        { label: 'Successful', value: stats.successfulTransactions }
      ],
      comingSoon: false,
      testId: 'finance-card-logs'
    },
    ...(isFullAdmin ? [{
      title: 'Plans & Subscriptions',
      description: 'Manage pricing plans, create coupons, and subscription settings',
      icon: Tag,
      path: '/admin/finances/plans',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      stats: [
        { label: 'Active Plans', value: stats.activePlans },
        { label: 'Active Coupons', value: stats.activeCoupons }
      ],
      comingSoon: false,
      testId: 'finance-card-plans'
    }, {
      title: 'Finance & Reward Settings',
      description: 'Configure Razorpay API keys and payment gateway settings',
      icon: Settings,
      path: '/admin/finances/settings',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      stats: [
        { label: 'Gateway', value: 'Razorpay' },
        { label: 'Status', value: isConfigured ? 'Configured' : 'Not Setup' }
      ],
      comingSoon: false,
      testId: 'finance-card-settings'
    }] : [])
  ];

  return (
    <Layout>
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <DollarSign className="w-8 h-8 text-green-600" />
          Financial Management
        </h1>
        <p className="text-gray-600 mt-2">Manage payments, subscriptions, and revenue tracking</p>
      </div>

      {/* Configuration Warning */}
      {isFullAdmin && !isConfigured && (
        <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
          <div className="flex items-start gap-3">
            <Settings className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-900 mb-1">Payment Gateway Not Configured</h3>
              <p className="text-sm text-yellow-800 mb-2">
                Configure Razorpay API keys to start accepting payments from users.
              </p>
              <button
                onClick={() => navigate('/admin/finances/settings')}
                className="text-sm font-medium text-yellow-900 underline hover:text-yellow-700"
              >
                Configure Now →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finance Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {financeCards.map((card, index) => (
          <Card
            key={index}
            className={`cursor-pointer hover:shadow-lg transition-all border-2 ${card.borderColor} ${card.comingSoon ? 'opacity-75' : ''}`}
            onClick={() => !card.comingSoon && navigate(card.path)}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${card.bgColor}`}>
                    <card.icon className={`w-6 h-6 ${card.color}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {card.title}
                      {card.comingSoon && (
                        <Badge variant="secondary" className="text-xs">
                          Coming Soon
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1 text-sm">
                      {card.description}
                    </CardDescription>
                  </div>
                </div>
                {!card.comingSoon && (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {card.stats.map((stat, idx) => (
                  <div key={idx}>
                    <p className="text-sm text-gray-600">{stat.label}</p>
                    <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
    </Layout>
  );
};

export default AdminFinancesPage;
